import { memo, useCallback, useRef, useState } from "react";
import { Button } from "@/components";
import { cn, layout } from "@/utilities";
import { CloudUploadIcon, TrashIcon } from "@/utilities/icon-utils";
import type { PreviewSize } from "@/types/core";

interface ImageSelectorProps {
  imageUrl?: string | null;
  onImageUpload: (file: File) => void;
  onImageRemove: () => void;
  className?: string;
  placeholder?: string;
  maxSizeInMB?: number;
  acceptedTypes?: string[];
  previewSize?: PreviewSize;
  showPreview?: boolean;
  disabled?: boolean;
  extendedClassname?: string;
}

export const ImageSelector = memo(
  ({
    imageUrl,
    onImageUpload,
    onImageRemove,
    className,
    placeholder = "Upload Image",
    maxSizeInMB = 5,
    acceptedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp"
    ],
    showPreview = true,
    disabled = false,
    extendedClassname
  }: ImageSelectorProps) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDragOver, setIsDragOver] = useState(false);

    // Handlers
    const handleFileSelect = useCallback(
      (file: File) => {
        if (disabled) return;

        // Validate file type
        if (!acceptedTypes.includes(file.type)) {
          const supportedFormats = acceptedTypes
            .map((type) => type.split("/")[1].toUpperCase())
            .join(", ");
          alert(`Please select a valid image file (${supportedFormats})`);
          return;
        }

        // Validate file size
        const maxSize = maxSizeInMB * 1024 * 1024;
        if (file.size > maxSize) {
          alert(`File size must be less than ${maxSizeInMB}MB`);
          return;
        }

        onImageUpload(file);
      },
      [onImageUpload, acceptedTypes, maxSizeInMB, disabled]
    );

    const handleFileInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
          handleFileSelect(file);
        }
        // Reset input value to allow selecting the same file again
        e.target.value = "";
      },
      [handleFileSelect]
    );

    const handleDragOver = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        if (!disabled) {
          setIsDragOver(true);
        }
      },
      [disabled]
    );

    const handleDragLeave = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
    }, []);

    const handleDrop = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);

        if (disabled) return;

        const file = e.dataTransfer.files[0];
        if (file) {
          handleFileSelect(file);
        }
      },
      [handleFileSelect, disabled]
    );

    const handleUploadClick = useCallback(() => {
      if (!disabled) {
        fileInputRef.current?.click();
      }
    }, [disabled]);

    const handleRemove = useCallback(() => {
      if (!disabled) {
        onImageRemove();
      }
    }, [onImageRemove, disabled]);

    // Render

    return (
      <div className={cn(layout.imageSelectorContainer, className)}>
        <div
          className={cn(
            layout.imageSelectorUploadArea,
            isDragOver && !disabled
              ? layout.imageSelectorBorderActive
              : layout.imageSelectorBorderDefault,
            imageUrl ? layout.imageSelectorBorderSolid : "",
            disabled ? layout.imageSelectorDisabled : "cursor-pointer"
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={!imageUrl ? handleUploadClick : undefined}
        >
          {imageUrl && showPreview ? (
            <div className="space-y-3">
              {/* Image Preview */}
              <div className="flex justify-center">
                <img
                  src={imageUrl}
                  alt="Selected image"
                  className={cn(layout.imageSelectorPreview, extendedClassname)}
                />
              </div>

              {/* Action Buttons */}
              <div className={cn(layout.imageSelectorButtonContainer)}>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleUploadClick}
                  className={cn(layout.imageSelectorButton)}
                  disabled={disabled}
                >
                  <CloudUploadIcon className="w-4 h-4" />
                  Change
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleRemove}
                  className={cn(layout.imageSelectorButtonRemove)}
                  disabled={disabled}
                >
                  <TrashIcon className="w-4 h-4" />
                  Remove
                </Button>
              </div>
            </div>
          ) : (
            <div className={cn(layout.imageSelectorUploadContent)}>
              <div className="flex justify-center">
                <CloudUploadIcon className="w-6 h-6" />
              </div>
              <div className={cn(layout.imageSelectorTextContainer)}>
                <p className={cn(layout.imageSelectorTextMedium)}>
                  {placeholder}
                </p>
                <p className={cn(layout.imageSelectorTextSmall)}>
                  Drag & drop or click to browse
                </p>
                <p className={cn(layout.imageSelectorTextMuted)}>
                  {acceptedTypes
                    .map((type) => type.split("/")[1].toUpperCase())
                    .join(", ")}{" "}
                  up to {maxSizeInMB}MB
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={(e) => {
                  // need to stop the event bubbling up so as to prevent multiple file selectors opened at once
                  e.stopPropagation();
                  handleUploadClick();
                }}
                disabled={disabled}
              >
                Choose File
              </Button>
            </div>
          )}
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedTypes.join(",")}
          onChange={handleFileInputChange}
          className="hidden"
          disabled={disabled}
        />
      </div>
    );
  }
);

ImageSelector.displayName = "ImageSelector";
